import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Text,
  Spinner,
  Icon,
  useColorModeValue,
  Portal
} from '@chakra-ui/react';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { useContainerVanTypeahead } from '../hooks/useContainerVanTypeahead';
import type { ContainerVan } from '../types/containerVan';

interface ContainerVanTypeaheadProps {
  placeholder?: string;
  onSelect?: (van: ContainerVan) => void;
  onChange?: (query: string) => void;
  companyId?: string;
  value?: string;
  isDisabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  maxResults?: number;
  usePortal?: boolean;
}

const ContainerVanTypeahead: React.FC<ContainerVanTypeaheadProps> = ({
  placeholder = "Search van number...",
  onSelect,
  onChange,
  companyId,
  value = '',
  isDisabled = false,
  size = 'md',
  maxResults = 10,
  usePortal = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    filteredVans,
    loading,
    setSearchQuery
  } = useContainerVanTypeahead(companyId);

  // Colors for light/dark mode
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');

  // Custom outside click handler for Portal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close if clicking on input container or dropdown
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Calculate dropdown position when it opens
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      
      // Close dropdown on scroll instead of following it
      const handleScroll = () => setIsOpen(false);
      const handleResize = () => {
        updateDropdownPosition();
      };
      
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  // Limit results for mobile performance
  const displayVans = filteredVans.slice(0, maxResults);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchQuery(newValue);
    setSelectedIndex(-1);
    setIsOpen(true);
    
    // Call onChange callback
    onChange?.(newValue);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleVanSelect = (van: ContainerVan) => {
    setInputValue(van.vanNumber);
    setSearchQuery(van.vanNumber);
    setIsOpen(false);
    setSelectedIndex(-1);
    
    // Call callbacks
    onChange?.(van.vanNumber);
    onSelect?.(van);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || displayVans.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < displayVans.length - 1 ? prev + 1 : 0
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : displayVans.length - 1
        );
        break;
      
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < displayVans.length) {
          handleVanSelect(displayVans[selectedIndex]);
        }
        break;
      
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const showDropdown = isOpen && !loading && (inputValue.length > 0 || displayVans.length > 0);
  const showNoResults = isOpen && !loading && inputValue.length > 0 && displayVans.length === 0;

  return (
    <Box 
      position="relative" 
      ref={containerRef} 
      w="100%" 
      zIndex={10000}
      sx={{
        isolation: 'isolate',
      }}
    >
      <InputGroup size={size}>
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          isDisabled={isDisabled}
          autoComplete="off"
          _focus={{
            borderColor: 'blue.500',
            boxShadow: '0 0 0 1px blue.500'
          }}
        />
        <InputRightElement>
          {loading ? (
            <Spinner size="sm" color="blue.500" />
          ) : (
            <Icon 
              as={isOpen ? MdExpandLess : MdExpandMore} 
              color="gray.400"
              cursor="pointer"
              onClick={() => setIsOpen(!isOpen)}
            />
          )}
        </InputRightElement>
      </InputGroup>

      {(showDropdown || showNoResults) && (
        usePortal ? (
          <Portal>
            <Box
              ref={dropdownRef}
              position="fixed"
              top={`${dropdownPosition.top + 4}px`}
              left={`${dropdownPosition.left}px`}
              width={`${dropdownPosition.width}px`}
              zIndex={99999}
              bg={bg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              boxShadow="2xl"
              maxH="200px"
              overflowY="auto"
              sx={{
                isolation: 'isolate',
                transform: 'translateZ(0)',
                backdropFilter: 'blur(0px)', // Creates new stacking context
              }}
            >
          {showNoResults ? (
            <Box p={3} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                No vans found matching "{inputValue}"
              </Text>
            </Box>
          ) : (
            <List spacing={0}>
              {displayVans.map((van, index) => (
                <ListItem
                  key={van.id}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={selectedIndex === index ? selectedBg : 'transparent'}
                  _hover={{ bg: hoverBg }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleVanSelect(van);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                  }}
                  borderBottom={index < displayVans.length - 1 ? "1px solid" : "none"}
                  borderColor={borderColor}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {van.vanNumber}
                  </Text>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        </Portal>
        ) : (
          <>
            {/* Backdrop for better visual separation */}
            <Box
              position="fixed"
              top={0}
              left={0}
              right={0}
              bottom={0}
              zIndex={98999}
              bg="transparent"
              onClick={() => setIsOpen(false)}
            />
            <Box
              ref={dropdownRef}
              position="absolute"
              top="100%"
              left={0}
              right={0}
              zIndex={99999}
              bg={bg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              boxShadow="2xl"
              maxH="200px"
              overflowY="auto"
              mt={2}
              sx={{
                isolation: 'isolate',
                transform: 'translateZ(0)',
              }}
            >
            {showNoResults ? (
              <Box p={3} textAlign="center">
                <Text fontSize="sm" color="gray.500">
                  No vans found matching "{inputValue}"
                </Text>
              </Box>
            ) : (
              <List spacing={0}>
                {displayVans.map((van, index) => (
                  <ListItem
                    key={van.id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    bg={selectedIndex === index ? selectedBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleVanSelect(van);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                    }}
                    borderBottom={index < displayVans.length - 1 ? "1px solid" : "none"}
                    borderColor={borderColor}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      {van.vanNumber}
                    </Text>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
          </>
        )
      )}
    </Box>
  );
};

export default ContainerVanTypeahead;